import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';

/**
 * GameSettingsContext - Global State Layer
 * Manages all game settings without touching DinoGame.tsx
 * 
 * Design Principles:
 * - DinoGame.tsx reads from context via refs, never re-renders on setting changes
 * - All state persists to localStorage automatically
 * - Settings are decoupled from game engine physics/rendering
 */

export type DifficultyMode = 'easy' | 'normal' | 'hard';
export type CosmeticType = 'default' | 'chrome' | 'retro' | 'neon' | 'gold';
export type ThemeMode = 'light' | 'dark';

export interface GameSettings {
  soundEnabled: boolean;
  difficultyMode: DifficultyMode;
  activeCosmetic: CosmeticType;
  tutorialCompleted: boolean;
  highScore: number;
  totalCoins: number;
  unlockedCosmetics: CosmeticType[];
  achievements: string[];
  lastPlayedDate: string;
  themeMode: ThemeMode;
}

export interface GameSettingsContextType {
  settings: GameSettings;
  updateSoundEnabled: (enabled: boolean) => void;
  updateDifficultyMode: (mode: DifficultyMode) => void;
  updateActiveCosmetic: (cosmetic: CosmeticType) => void;
  updateHighScore: (score: number) => void;
  addCoins: (amount: number) => void;
  unlockCosmetic: (cosmetic: CosmeticType) => void;
  addAchievement: (achievementId: string) => void;
  markTutorialCompleted: () => void;
  resetAllSettings: () => void;
  updateThemeMode: (mode: ThemeMode) => void;
}

const DEFAULT_SETTINGS: GameSettings = {
  soundEnabled: true,
  difficultyMode: 'normal',
  activeCosmetic: 'default',
  tutorialCompleted: false,
  highScore: 0,
  totalCoins: 0,
  unlockedCosmetics: ['default'],
  achievements: [],
  lastPlayedDate: new Date().toISOString(),
  themeMode: 'light',
};

const GameSettingsContext = createContext<GameSettingsContextType | undefined>(undefined);

export const GameSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<GameSettings>(DEFAULT_SETTINGS);
  const [isHydrated, setIsHydrated] = useState(false);

  // Hydrate from localStorage on mount (before game initializes)
  useEffect(() => {
    const stored = localStorage.getItem('gameSettings');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSettings((prev) => ({ ...prev, ...parsed }));
        // Apply theme immediately on hydration
        if (typeof document !== 'undefined' && parsed.themeMode) {
          document.documentElement.setAttribute('data-theme', parsed.themeMode);
          // Also sync .dark class for Tailwind dark: variants
          if (parsed.themeMode === 'dark') {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
        }
      } catch (e) {
        console.error('[GameSettings] Failed to parse stored settings:', e);
      }
    }
    setIsHydrated(true);
  }, []);

  // Persist to localStorage whenever settings change
  useEffect(() => {
    if (isHydrated) {
      localStorage.setItem('gameSettings', JSON.stringify(settings));
      // Update DOM theme attribute and .dark class when theme changes
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', settings.themeMode);
        // Also sync .dark class for Tailwind dark: variants
        if (settings.themeMode === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    }
  }, [settings, isHydrated]);

  const updateSoundEnabled = useCallback((enabled: boolean) => {
    setSettings((prev) => ({ ...prev, soundEnabled: enabled }));
  }, []);

  const updateDifficultyMode = useCallback((mode: DifficultyMode) => {
    setSettings((prev) => ({ ...prev, difficultyMode: mode }));
  }, []);

  const updateActiveCosmetic = useCallback((cosmetic: CosmeticType) => {
    setSettings((prev) => ({ ...prev, activeCosmetic: cosmetic }));
  }, []);

  const updateHighScore = useCallback((score: number) => {
    setSettings((prev) => ({
      ...prev,
      highScore: Math.max(prev.highScore, score),
    }));
  }, []);

  const addCoins = useCallback((amount: number) => {
    setSettings((prev) => ({
      ...prev,
      totalCoins: prev.totalCoins + amount,
    }));
  }, []);

  const unlockCosmetic = useCallback((cosmetic: CosmeticType) => {
    setSettings((prev) => {
      if (prev.unlockedCosmetics.includes(cosmetic)) {
        return prev;
      }
      return {
        ...prev,
        unlockedCosmetics: [...prev.unlockedCosmetics, cosmetic],
      };
    });
  }, []);

  const addAchievement = useCallback((achievementId: string) => {
    setSettings((prev) => {
      if (prev.achievements.includes(achievementId)) {
        return prev;
      }
      return {
        ...prev,
        achievements: [...prev.achievements, achievementId],
      };
    });
  }, []);

  const markTutorialCompleted = useCallback(() => {
    setSettings((prev) => ({ ...prev, tutorialCompleted: true }));
  }, []);

  const updateThemeMode = useCallback((mode: ThemeMode) => {
    setSettings((prev) => ({ ...prev, themeMode: mode }));
  }, []);

  const resetAllSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    localStorage.removeItem('gameSettings');
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, []);

  const value: GameSettingsContextType = {
    settings,
    updateSoundEnabled,
    updateDifficultyMode,
    updateActiveCosmetic,
    updateHighScore,
    addCoins,
    unlockCosmetic,
    addAchievement,
    markTutorialCompleted,
    resetAllSettings,
    updateThemeMode,
  };

  return (
    <GameSettingsContext.Provider value={value}>
      {children}
    </GameSettingsContext.Provider>
  );
};

export const useGameSettings = (): GameSettingsContextType => {
  const context = useContext(GameSettingsContext);
  if (!context) {
    throw new Error('useGameSettings must be used within GameSettingsProvider');
  }
  return context;
};

export const useTheme = () => {
  const { settings, updateThemeMode } = useGameSettings();
  
  // Register keyboard shortcut (Ctrl/Cmd + Shift + D) on mount
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform);
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      
      if (modKey && e.shiftKey && e.code === 'KeyD') {
        e.preventDefault();
        const newTheme = settings.themeMode === 'light' ? 'dark' : 'light';
        updateThemeMode(newTheme);
        
        // Track theme toggle via GA4
        if (typeof window !== 'undefined' && (window as any).trackThemeToggle) {
          (window as any).trackThemeToggle(newTheme);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [settings.themeMode, updateThemeMode]);
  
  return {
    theme: settings.themeMode,
    toggleTheme: () => {
      const newTheme = settings.themeMode === 'light' ? 'dark' : 'light';
      updateThemeMode(newTheme);
      
      // Track theme toggle via GA4
      if (typeof window !== 'undefined' && (window as any).trackThemeToggle) {
        (window as any).trackThemeToggle(newTheme);
      }
    },
    setTheme: updateThemeMode,
  };
};
